// Content tab — the family/teen/mature rating cards. Split out of
// CustomizeTabs.tsx.

import type { ContentRating } from '@aod/protocol';
import { cn } from '@aod/ui/lib/utils';
import { Heart, Shield, ShieldAlert } from 'lucide-react';
import { type KeyboardEvent, useRef } from 'react';
import { useT } from '../../i18n/index.js';
import type { TabProps } from '../CustomizeTabs.js';
import { nextRovingIndex } from '../controls.js';
import { setContent } from '../setupModel.js';

// ── Content ────────────────────────────────────────────────────────────

const RATINGS: readonly { value: ContentRating; Icon: typeof Heart }[] = [
  { value: 'family', Icon: Heart },
  { value: 'teen', Icon: Shield },
  { value: 'mature', Icon: ShieldAlert },
];

export function ContentTab({ draft, onChange }: TabProps) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const value = draft.ruleset.content;
  const pick = (v: ContentRating) => onChange(setContent(draft, v));
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const i = RATINGS.findIndex((r) => r.value === value);
    const next = nextRovingIndex(e.key, i, RATINGS.length);
    if (next === null) return;
    e.preventDefault();
    const r = RATINGS[next];
    if (!r) return;
    pick(r.value);
    ref.current?.querySelectorAll<HTMLElement>('[role="radio"]')[next]?.focus();
  };
  return (
    <div className="flex flex-col gap-3">
      <h3 id="setup-content-label" className="text-sm font-medium text-foreground">
        {t('setup.content.label')}
      </h3>
      <div
        ref={ref}
        role="radiogroup"
        aria-labelledby="setup-content-label"
        onKeyDown={onKeyDown}
        className="grid gap-3 sm:grid-cols-3"
      >
        {RATINGS.map(({ value: v, Icon }) => {
          const selected = v === value;
          return (
            <button
              key={v}
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: card-styled radio
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => pick(v)}
              className={cn(
                'flex cursor-pointer flex-col gap-1 rounded-xl border p-4 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                selected
                  ? 'border-primary bg-primary/10 ring-1 ring-primary'
                  : 'border-border bg-card/70 hover:bg-muted/60',
              )}
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Icon
                  className={cn('size-4', selected ? 'text-primary' : 'text-muted-foreground')}
                  aria-hidden
                />
                {t(`setup.content.${v}`)}
              </span>
              <span className="text-xs text-muted-foreground">{t(`setup.content.${v}.desc`)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
